/**
 * Detects common data quality anomalies in time-series data.
 * Used to provide contextual hints to users about potential data quality issues.
 */

export type DataAnomaly = {
  type: 'flat_line' | 'constant_zero';
  startTime: number;
  endTime: number;
  description: string;
};

type DataPoint = [number, number];

const FLAT_LINE_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours
const FLAT_LINE_TOLERANCE = 0.001; // Allow tiny variations

/**
 * Detects periods where data remains constant (flat-line).
 */
function detectFlatLines(data: DataPoint[]): DataAnomaly[] {
  if (data.length < 2) return [];

  const anomalies: DataAnomaly[] = [];
  let flatLineStart: number | null = null;
  let flatLineValue: number | null = null;

  for (let i = 0; i < data.length; i++) {
    const [timestamp, value] = data[i];

    if (flatLineStart === null) {
      flatLineStart = timestamp;
      flatLineValue = value;
      continue;
    }

    const isFlat = Math.abs(value - flatLineValue!) < FLAT_LINE_TOLERANCE;

    if (!isFlat) {
      // Check if the flat period was long enough to be notable
      const duration = timestamp - flatLineStart;
      if (duration >= FLAT_LINE_THRESHOLD_MS) {
        anomalies.push({
          type: flatLineValue === 0 ? 'constant_zero' : 'flat_line',
          startTime: flatLineStart,
          endTime: data[i - 1][0],
          description:
            flatLineValue === 0
              ? 'Constant zero value detected - this indicates missing or unavailable data from the source.'
              : `Constant value (${flatLineValue?.toFixed(2)}) detected - this reflects the upstream data source.`,
        });
      }
      flatLineStart = timestamp;
      flatLineValue = value;
    }
  }

  // Check final segment
  if (flatLineStart !== null && data.length > 0) {
    const lastPoint = data[data.length - 1];
    const duration = lastPoint[0] - flatLineStart;
    if (duration >= FLAT_LINE_THRESHOLD_MS) {
      anomalies.push({
        type: flatLineValue === 0 ? 'constant_zero' : 'flat_line',
        startTime: flatLineStart,
        endTime: lastPoint[0],
        description:
          flatLineValue === 0
            ? 'Constant zero value detected - this indicates missing or unavailable data from the source.'
            : `Constant value (${flatLineValue?.toFixed(2)}) detected - this reflects the upstream data source.`,
      });
    }
  }

  return anomalies;
}

/**
 * Analyzes time-series data for quality anomalies.
 * Returns an array of detected anomalies that can be used for visual hints.
 */
export function detectDataAnomalies(data: DataPoint[]): DataAnomaly[] {
  if (!data || data.length === 0) return [];

  // Sort data by timestamp to ensure correct analysis
  const sortedData = [...data].sort((a, b) => a[0] - b[0]);

  return detectFlatLines(sortedData);
}

export type AnomalySummary = {
  /** Pre-split lines for display rendering */
  lines: string[];
  /** Single-line version for logging/accessibility */
  raw: string;
};

/**
 * Creates a structured summary for detected anomalies.
 * Returns null if no anomalies are found.
 */
export function summarizeAnomalies(
  anomalies: DataAnomaly[],
): AnomalySummary | null {
  if (anomalies.length === 0) return null;

  const count = anomalies.length;
  const detectedLine = `Detected: ${count} constant value period${count > 1 ? 's' : ''}.`;
  const contextLine = 'This reflects the data as received from the source.';

  return {
    lines: [detectedLine, contextLine],
    raw: `${detectedLine} ${contextLine}`,
  };
}
